import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { fetchReservationMarketSegment, updateReservationMarketSegment } from '@/apis/aipass'
import * as MarketSegmentModel from '@/models/reservation-market-segment'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { EditFooter } from '@/components/organisms/edit-footer'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { AddFormFieldButton } from '@/components/molecules/add-form-field-button'
import { useSettingsEditPageGuardEffect } from '@/hooks/use-settings-edit-guard'

type MarketSegmentType = Partial<MarketSegmentModel.MarketSegmentType>

export const SettingReservationMarketSegmentEdit: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { errorHandler } = useErrorHandler()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const initialMarketSegment: MarketSegmentType = {
    id: undefined,
    code: undefined,
    name: undefined,
    groupType: MarketSegmentModel.GroupType.Individual,
  }

  const {
    control,
    handleSubmit,
    register,
    formState: { isValid },
  } = useForm<{ marketSegments: MarketSegmentType[] }>({
    defaultValues: {
      marketSegments: [initialMarketSegment],
    },
  })
  const inputSegments = useFieldArray({
    control,
    name: 'marketSegments',
    keyName: 'inputKey',
  })

  const onSaveMarketSegments = async (formValue: { marketSegments: MarketSegmentType[] }) => {
    try {
      setIsLoading(true)
      await updateReservationMarketSegment(formValue.marketSegments)
      history.push({ pathname: '/setting/reservation/market-segment' })
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  useSettingsEditPageGuardEffect({
    redirectPath: '/setting/reservation/market-segment',
    onAllowed: () => fetchReservationMarketSegment().then(inputSegments.replace),
  })

  const footerContent = <EditFooter onCancel={history.goBack} onSave={handleSubmit(onSaveMarketSegments)} isDisabledSave={!isValid} />
  return (
    <SettingsLayout loading={isLoading} footerContent={footerContent}>
      <div css={settingEditContainerStyle}>
        <div className="container-title">
          <p>{t('Segment')}</p>
        </div>
        <div css={settingEditHeaderStyle}>
          <div css={segmentCodeWidthStyle}>{t('Segment code')}</div>
          <div css={nameWidthStyle}>{t('Name')}</div>
          <div css={groupWidthStyle} style={{ marginRight: 30 }}>
            {t('Group')}
          </div>
        </div>
        <div
          css={settingEditFormStyle}
          style={{
            // 55px(header), 24px(padding), 50px(title), 59px(footer), 32px(th)
            height: window.innerHeight - 55 - 24 * 2 - 50 - 59 - 32,
            overflowY: 'auto',
          }}
        >
          <ul>
            {inputSegments.fields.map((marketSegment, index) => (
              <li key={`${marketSegment.id}${index}`}>
                <div css={segmentCodeWidthStyle}>
                  <input
                    {...register(`marketSegments.${index}.code`, { required: true })}
                    type="text"
                    placeholder="0000"
                    css={inputStyle}
                    disabled={!!marketSegment.id}
                  />
                </div>
                <div css={nameWidthStyle}>
                  <input
                    {...register(`marketSegments.${index}.name`, { required: true })}
                    type="text"
                    placeholder={t('Segment')}
                    css={inputStyle}
                  />
                </div>
                <Controller
                  name={`marketSegments.${index}.groupType`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      value={value || MarketSegmentModel.GroupType.Individual}
                      onChange={onChange}
                      customStyle={css(groupWidthStyle, { paddingBottom: 0 })}
                    >
                      <AntSelect.Option value={MarketSegmentModel.GroupType.Individual}>{t('Individual')}</AntSelect.Option>
                      <AntSelect.Option value={MarketSegmentModel.GroupType.Group}>{t('Group')}</AntSelect.Option>
                    </Select>
                  )}
                />
                <DeleteIcon onClick={() => inputSegments.remove(index)} />
              </li>
            ))}
          </ul>
          <AddFormFieldButton
            onAdd={() => inputSegments.append(initialMarketSegment)}
            label={t('Add segment')}
            className="plus-button-block"
          />
        </div>
      </div>
    </SettingsLayout>
  )
}

const settingEditContainerStyle = css({
  height: '100%',
  boxShadow: '0px 0px 6px #0000001A',
  borderRadius: 5,
  background: '#FFF',
  '.container-title': {
    height: 50,
    padding: '16px 32px',
    borderBottom: '1px solid #F2F2F2',
    p: {
      fontSize: 18,
      fontWeight: 'bold',
    },
  },
})

const segmentCodeWidthStyle = css({ width: '15%' })
const nameWidthStyle = css({ flex: 1 })
const groupWidthStyle = css({ width: '15%' })

const settingEditHeaderStyle = css({
  background: '#F5F5F5',
  color: '#676767',
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  padding: '10px 32px',
})

const settingEditFormStyle = css({
  padding: '24px 32px',
  li: {
    display: 'flex',
    padding: '0 0 16px 0',
    '> div': { paddingRight: 16 },
  },
  '.plus-button-block': {
    background: 'none',
    border: 'none',
    justifyContent: 'left',
    marginTop: 0,
    width: 'fit-content',
    ':hover': { background: 'none' },
  },
})

const inputStyle = css({
  width: '100%',
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  padding: '0 16px',
  '&::placeholder': { color: '#CCCCCC' },
})
